import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';

export default function CaseList() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    API.get('/cases').then(res => {
      setCases(res.data);
      setLoading(false);
    });
  }, []);

  const statusColor = (s) => {
    if (s === 'pending_upload') return { bg: '#fef3c7', text: '#d97706' };
    if (s === 'uploaded') return { bg: '#dbeafe', text: '#1d4ed8' };
    if (s === 'report_ready') return { bg: '#dcfce7', text: '#16a34a' };
    return { bg: '#f3f4f6', text: '#6b7280' };
  };

  const handleDownloadReport = async (e, caseId, patientName) => {
    e.stopPropagation(); // prevent row click navigation
    setDownloading(caseId);
    try {
      const res = await API.get(`/reports/${caseId}/download-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Report_${patientName}_Case${caseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Report not available yet');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <button style={styles.back} onClick={() => navigate('/dashboard')}>← Back</button>
          <h2 style={styles.title}>Patient Cases</h2>
        </div>
        {(user.role === 'clinic' || user.role === 'admin') && (
          <button style={styles.newBtn} onClick={() => navigate('/cases/new')}>+ New Case</button>
        )}
      </div>

      {loading ? (
        <p style={{ color: '#888' }}>Loading cases...</p>
      ) : (
        <div style={styles.table}>
          {/* Table Header */}
          <div style={styles.tableHeader}>
            <span>Patient</span>
            <span>Patch Serial</span>
            <span>Clinic</span>
            <span>Status</span>
            <span>Date</span>
            <span>Report</span>
          </div>

          {cases.length === 0 && (
            <p style={styles.empty}>No cases found. Create your first case.</p>
          )}

          {cases.map(c => {
            const sc = statusColor(c.status);
            const reportReady = c.status === 'report_ready';
            return (
              <div
                key={c.id}
                style={styles.row}
                onClick={() => navigate(`/cases/${c.id}`)}
              >
                {/* Patient name */}
                <span style={styles.patientName}>{c.patient_name}</span>

                {/* Patch serial */}
                <span style={styles.mutedText}>{c.patch_serial}</span>

                {/* Clinic */}
                <span style={styles.mutedText}>{c.clinic_name || 'N/A'}</span>

                {/* Status badge */}
                <span>
                  <span style={{ ...styles.badge, background: sc.bg, color: sc.text }}>
                    {c.status?.replace(/_/g, ' ')}
                  </span>
                </span>

                {/* Date */}
                <span style={styles.mutedText}>
                  {new Date(c.created_at).toLocaleDateString()}
                </span>

                {/* Report download — show for everyone when ready, or pending message for clinic */}
                <span onClick={e => e.stopPropagation()}>
                  {reportReady && user.role !== 'analyst' ? (
                    <button
                      style={styles.downloadBtn}
                      onClick={e => handleDownloadReport(e, c.id, c.patient_name)}
                      disabled={downloading === c.id}
                    >
                      {downloading === c.id ? 'Downloading...' : 'Download PDF'}
                    </button>
                  ) : (
                    <span style={styles.pendingText}>
                      {c.status === 'pending_upload' ? 'Awaiting upload' :
                       c.status === 'uploaded' ? 'Under review' : '—'}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { maxWidth: '1200px', margin: '0 auto', padding: '40px 32px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '28px' },
  back: { background: 'none', border: 'none', color: '#0ea5e9', cursor: 'pointer', fontSize: '13px', marginBottom: '6px', padding: 0, display: 'block', fontFamily: "'DM Sans', sans-serif" },
  title: { fontSize: '22px', fontWeight: '700', color: '#0f172a', letterSpacing: '-0.4px' },
  newBtn: {
    background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    color: '#fff', border: 'none', padding: '10px 20px',
    borderRadius: '10px', cursor: 'pointer', fontSize: '14px',
    fontWeight: '600', boxShadow: '0 4px 12px rgba(14,165,233,0.3)',
    fontFamily: "'DM Sans', sans-serif",
  },
  table: { background: 'white', borderRadius: '16px', boxShadow: '0 4px 16px rgba(0,0,0,0.06)', overflow: 'hidden', border: '1px solid #f1f5f9' },
  tableHeader: {
    display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1.2fr 1fr 1.5fr',
    padding: '14px 24px', background: '#f8fafc',
    fontSize: '11px', fontWeight: '700', color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.8px',
    borderBottom: '1px solid #f1f5f9',
  },
  row: {
    display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1.2fr 1fr 1.5fr',
    padding: '16px 24px', borderTop: '1px solid #f8fafc',
    cursor: 'pointer', alignItems: 'center',
    transition: 'background 0.15s',
  },
  patientName: { fontSize: '14px', fontWeight: '600', color: '#0f172a' },
  mutedText: { fontSize: '13px', color: '#94a3b8' },
  badge: { padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', display: 'inline-block' },
  downloadBtn: {
    padding: '7px 14px', background: 'linear-gradient(135deg, #10b981, #059669)',
    color: '#fff', border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '12px', fontWeight: '600',
    whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
    fontFamily: "'DM Sans', sans-serif",
  },
  pendingText: { fontSize: '12px', color: '#cbd5e1', fontStyle: 'italic' },
  empty: { padding: '60px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' },
};
